'use client';

import React, { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface TeamMember {
  name: string;
  role: string;
  image: string;
  alt: string;
  bio: string;
  specialization: string;
  contributions: string[];
}

interface TeamSectionProps {
  team: TeamMember[];
}

const TeamSection: React.FC<TeamSectionProps> = ({ team }) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedMember, setSelectedMember] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <section className="py-10 sm:py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Our Dedicated Team
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Passionate professionals committed to making a difference
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 max-w-6xl mx-auto">
            {team.map((_, index) => (
              <div key={index} className="bg-card rounded-xl shadow-subtle overflow-hidden">
                <div className="h-64 bg-muted animate-pulse" />
                <div className="p-6 space-y-2">
                  <div className="h-6 bg-muted animate-pulse rounded" />
                  <div className="h-4 bg-muted animate-pulse rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-10 sm:py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Our Dedicated Team
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Passionate professionals committed to making a difference
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 max-w-6xl mx-auto">
          {team.map((member, index) => (
            <div
              key={index}
              className="bg-card rounded-xl shadow-subtle hover:shadow-elevated transition-all duration-300 overflow-hidden group cursor-pointer"
              onClick={() => setSelectedMember(selectedMember === index ? null : index)}
            >
              <div className="relative h-64 overflow-hidden">
                <AppImage
                  src={member.image}
                  alt={member.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="text-sm font-semibold">{member.specialization}</p>
                </div>
              </div>

              <div className="p-4 sm:p-6">
                <h3 className="text-xl font-heading font-semibold text-foreground mb-1">
                  {member.name}
                </h3>
                <p className="text-sm text-primary font-medium mb-3">{member.role}</p>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  {member.bio}
                </p>

                {selectedMember === index && (
                  <div className="mt-4 pt-4 border-t border-border space-y-3">
                    <h4 className="text-sm font-semibold text-foreground flex items-center">
                      <Icon name="SparklesIcon" size={16} className="text-secondary mr-2" />
                      Key Contributions
                    </h4>
                    <ul className="space-y-2">
                      {member.contributions.map((contribution, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <Icon name="CheckCircleIcon" size={16} className="text-secondary mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{contribution}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <button
                  className="mt-4 text-sm text-primary font-semibold flex items-center hover:underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedMember(selectedMember === index ? null : index);
                  }}
                >
                  {selectedMember === index ? 'Show Less' : 'Learn More'}
                  <Icon
                    name="ChevronDownIcon"
                    size={16}
                    className={`ml-1 transition-transform duration-300 ${
                      selectedMember === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;